import { useState, useEffect, useRef } from "react";
import { Modal } from "antd";
import { useTranslation } from "react-i18next";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { marked } from "marked";
import DOMPurify from "dompurify";
import TurndownService from "turndown";
import { ReactMarkdownWithGFMPlugin } from "@/utils";
import InfoModal from "@/components/ui/InfoModal";
import BrandButton from "@/components/ui/BrandButton";
import SectionHeading from "@/components/ui/SectionHeading";
import ErrorBoundary from "@/components/ui/ErrorBoundary";

// Initialize turndown for HTML -> Markdown conversion
const turndownService = new TurndownService({
  headingStyle: "atx",
  bulletListMarker: "-",
  codeBlockStyle: "fenced",
});

const MARKDOWN_SANITIZE_CONFIG = {
  USE_PROFILES: { html: true },
  FORBID_TAGS: ["style", "svg", "math"],
  FORBID_ATTR: ["style"],
};

const MARKDOWN_SYNC_DEBOUNCE_MS = 150;

const markdownToSafeHtml = (markdown) => {
  if (!markdown) return "";
  const parsedHtml = marked.parse(markdown);
  return DOMPurify.sanitize(parsedHtml, MARKDOWN_SANITIZE_CONFIG);
};

const SideBySideEditors = ({
  feedbacks,
  isLoadingBrief,
  isRegenerateModalVisible,
  setIsRegenerateModalVisible,
  handleRegenerateBrief,
  contentBrief,
  contentDraft,
  setContentDraft,
  onHtmlValueChange,
}) => {
  const { t } = useTranslation();
  const getLocalizedOption = (option) => {
    if (!option) return "";
    const key = option.replace(/[^a-zA-Z0-9]/g, "");
    return t(`contentOptimizer.feedbackOptions.${key}`, option);
  };
  // HTML content for the Quill editor
  const [htmlValue, setHtmlValue] = useState(() => {
    try {
      return markdownToSafeHtml(contentDraft);
    } catch (e) {
      console.error("[SideBySideEditors] error parsing markdown on init", e);
      return "";
    }
  });

  // Track markdown emitted from editor so parent echoes can be ignored
  const lastEditorMarkdown = useRef(contentDraft ?? "");
  const quillRef = useRef(null);
  const editorContainerRef = useRef(null);
  const markdownSyncTimeoutRef = useRef(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const editorRoot =
        quillRef.current?.getEditor?.()?.root ??
        editorContainerRef.current?.querySelector(".ql-editor");
      if (!editorRoot) {
        return;
      }
      editorRoot.setAttribute("aria-labelledby", "content-editor-label");
    }, 0);

    return () => clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (markdownSyncTimeoutRef.current) {
        clearTimeout(markdownSyncTimeoutRef.current);
        markdownSyncTimeoutRef.current = null;
      }
    };
  }, []);

  // Sync markdown from parent to HTML in editor
  useEffect(() => {
    const nextContentDraft = contentDraft ?? "";

    if (nextContentDraft === lastEditorMarkdown.current) {
      return;
    }

    if (nextContentDraft === "") {
      setHtmlValue("");
      onHtmlValueChange?.("");
      return;
    }

    if (markdownSyncTimeoutRef.current) {
      clearTimeout(markdownSyncTimeoutRef.current);
      markdownSyncTimeoutRef.current = null;
    }

    try {
      const html = markdownToSafeHtml(nextContentDraft);
      setHtmlValue(html);
      onHtmlValueChange?.(html);
    } catch (e) {
      console.error(
        "[SideBySideEditors] error syncing contentDraft to htmlValue",
        e,
      );
    }
  }, [contentDraft, onHtmlValueChange]);

  // Handle editor changes: convert HTML to markdown and update parent
  const handleChange = (html) => {
    setHtmlValue(html);
    onHtmlValueChange?.(html);

    if (markdownSyncTimeoutRef.current) {
      clearTimeout(markdownSyncTimeoutRef.current);
    }

    markdownSyncTimeoutRef.current = setTimeout(() => {
      try {
        const markdown = html ? turndownService.turndown(html) : "";
        lastEditorMarkdown.current = markdown;
        if (isMountedRef.current) {
          setContentDraft(markdown);
        }
      } catch (e) {
        console.error(
          "[SideBySideEditors] error converting HTML to markdown",
          e,
        );
      }
    }, MARKDOWN_SYNC_DEBOUNCE_MS);
  };

  return (
    <div className="d-flex gap-3">
      <div style={{ flex: 1, minWidth: 0 }}>
        <SectionHeading className="mb-2" data-testid="content-brief-title">
          {t("contentOptimizer.contentBrief", "Content Brief")}
        </SectionHeading>
        <div
          className="card-radius"
          style={{
            border: "1px solid var(--border)",
            padding: "1.5rem",
            height: "500px",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            backgroundColor: "var(--bg-elevated)",
            opacity: isLoadingBrief ? 0.5 : 1,
            pointerEvents: isLoadingBrief ? "none" : "auto",
            transition: "opacity 0.2s ease",
          }}
          data-testid="content-brief-renderer"
        >
          <div className="mb-3 d-flex align-items-center gap-2">
            <InfoModal
              label={t("contentOptimizer.feedbackInfo", "Feedback Info")}
              info={t(
                "contentOptimizer.feedbackInfoText",
                "Like or dislike what you see? Select/highlight any part of the content brief and provide feedback to help us improve.",
              )}
            />
            <BrandButton
              onClick={() => setIsRegenerateModalVisible(true)}
              disabled={feedbacks.length === 0 || isLoadingBrief}
              className={""}
              dataTestId="regenerate-brief-button"
            >
              {isLoadingBrief ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  {t(
                    "contentOptimizer.generatingBrief",
                    "Generating Brief...",
                  )}
                </>
              ) : (
                t(
                  "contentOptimizer.regenerateBrief",
                  "Re-Generate Brief with Feedback",
                )
              )}
            </BrandButton>
            <Modal
              title={t(
                "contentOptimizer.confirmRegenerate",
                "Confirm Brief Re-Generation",
              )}
              open={isRegenerateModalVisible}
              onCancel={() => setIsRegenerateModalVisible(false)}
              footer={[
                <BrandButton
                  key="confirm"
                  className="bg-brand-gradient"
                  onClick={() => {
                    setIsRegenerateModalVisible(false);
                    handleRegenerateBrief();
                  }}
                >
                  {t(
                    "contentOptimizer.regenerateBriefBtn",
                    "Re-Generate Brief",
                  )}
                </BrandButton>,
              ]}
            >
              <div>
                <h6>
                  {t(
                    "contentOptimizer.regeneratePrompt",
                    "The brief will be re-generated with the following feedback(s):",
                  )}
                </h6>
                {feedbacks.length === 0 ? (
                  <div className="text-secondary">
                    {t(
                      "contentOptimizer.noFeedbackProvided",
                      "No feedbacks provided.",
                    )}
                  </div>
                ) : (
                  <ul className="feedback-modal-list">
                    {feedbacks.map((fb, idx) => (
                      <li key={idx} className="feedback-modal-list-item">
                        <div className="feedback-modal-list-text">
                          {fb.text}
                        </div>
                        {fb.impressions && fb.impressions.length > 0 && (
                          <ul className="feedback-modal-list-impressions">
                            {fb.impressions.map((imp, i) => (
                              <li key={i}>{getLocalizedOption(imp)}</li>
                            ))}
                          </ul>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </Modal>
          </div>
          <div
            className="feedback-content-brief"
            style={{
              flex: "1 1 auto",
              overflow: "auto",
              paddingRight: "0.5rem",
            }}
          >
            <ReactMarkdownWithGFMPlugin>
              {contentBrief}
            </ReactMarkdownWithGFMPlugin>
          </div>
        </div>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <SectionHeading
          id="content-editor-label"
          className="mb-2"
          data-testid="content-editor-title"
        >
          {t("contentOptimizer.contentEditor", "Content Editor")}
        </SectionHeading>
        <div
          style={{
            border: "1px solid var(--border)",
            padding: "1.5rem",
            height: "500px",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            backgroundColor: "var(--bg-elevated)",
          }}
          data-testid="content-editor-wrapper"
          className="quill-editor-container card-radius"
          ref={editorContainerRef}
        >
          <ErrorBoundary
            onError={() => onHtmlValueChange?.(htmlValue)}
            fallback={
              <div
                className="d-flex flex-column h-100"
                data-testid="content-editor-fallback-container"
              >
                <div
                  className="alert alert-warning py-1 px-2 mb-2 small d-flex align-items-center"
                  role="alert"
                  data-testid="content-editor-fallback-notice"
                >
                  <span>
                    {t(
                      "contentOptimizer.editorFallbackNotice",
                      "Rich text editor encountered an issue. Using plain text editor.",
                    )}
                  </span>
                </div>
                <textarea
                  className="form-control flex-grow-1"
                  style={{
                    resize: "none",
                    fontFamily: "inherit",
                    backgroundColor: "var(--bg-card, #ffffff)",
                    borderColor: "var(--border, #e5e7eb)",
                    color: "var(--text-primary, #111827)",
                  }}
                  data-testid="content-editor-fallback-textarea"
                  value={contentDraft ?? ""}
                  onChange={(e) => {
                    const nextVal = e.target.value;
                    setContentDraft(nextVal);
                    try {
                      onHtmlValueChange?.(markdownToSafeHtml(nextVal));
                    } catch {
                      onHtmlValueChange?.(nextVal);
                    }
                  }}
                  placeholder={t(
                    "contentOptimizer.editorPlaceholder",
                    "No content available. Click on 'Suggest Draft' below.",
                  )}
                  aria-labelledby="content-editor-label"
                />
              </div>
            }
          >
            <ReactQuill
              ref={quillRef}
              value={htmlValue}
              onChange={handleChange}
              theme="snow"
              placeholder={t(
                "contentOptimizer.editorPlaceholder",
                "No content available. Click on 'Suggest Draft' below.",
              )}
              modules={{
                toolbar: [
                  [{ header: [1, 2, 3, false] }],
                  ["bold", "italic", "underline", "strike"],
                  ["blockquote", "code-block"],
                  [{ list: "ordered" }, { list: "bullet" }],
                  ["link"],
                  ["clean"],
                ],
              }}
            />
          </ErrorBoundary>
          <textarea
            value={contentDraft ?? ""}
            readOnly
            aria-hidden="true"
            tabIndex={-1}
            style={{ display: "none" }}
            data-testid="content-editor-textarea"
          />
        </div>
      </div>
    </div>
  );
};

export default SideBySideEditors;
